import type { SiteLogistics } from '@interfaces/ILogistics';
import { fold } from '@utils/text';
import { buildDeliveredAmounts } from './deliveryAmounts';
import type { StockFormPlumbing } from './panels/StockDetailDrawer';

export { openStockFor, materialPrefillFor, openOrderNoticeFor, stockUnitFor } from '@d-orca/shared/client/logistics';

/**
 * The stock form's plumbing (`StockFormPlumbing`), derived from the site's
 * logistics payload — the same inputs every host of `StockEntryForm` wires:
 * the Logistics page's stock dialog and stock drawer, the site map's stock
 * companion, and the task drawer's stock companion. One place, so a host
 * cannot drift on which fold keys the lead map or which materials feed it.
 *
 * Deliberately NOT memoised: the React Compiler memoises call sites
 * automatically, and hand-written memoization it cannot preserve fails the
 * `react-hooks` compiler rule outright.
 */
export function stockFormPlumbingFor(siteId: string, data: SiteLogistics): StockFormPlumbing {
  /* Keyed by `fold(...)`, the same normalisation every material lookup uses —
     keyed raw, a case or width difference silently missed a material the rest
     of the dialog matched. */
  const leadsByMaterial = Object.fromEntries([
    // Untracked materials first, so a tracked row's own lead wins the key if a
    // material somehow appears in both.
    //
    // They belong here at all because the dialog's material combobox offers
    // every DELIVERY item, not just the tracked ones — picking one that has no
    // stock row yet used to fall through to the site average under a caption
    // reading "no delivery history for this material", about a material whose
    // delivery had already landed.
    ...data.untracked
      .filter((m) => m.leadTimeDays != null)
      .map(
        (m) =>
          [fold(m.materialName), { days: m.leadTimeDays!, source: m.leadSource ?? 'site-median' }] as const,
      ),
    ...data.inventory
      .filter((l) => l.leadTimeDays != null)
      .map(
        (l) =>
          [fold(l.materialName), { days: l.leadTimeDays!, source: l.leadSource ?? 'site-median' }] as const,
      ),
  ]);
  return {
    siteId,
    siteMedianLeadDays: data.siteMedianLeadDays,
    leadsByMaterial,
    unitsByMaterial: Object.fromEntries(data.inventory.map((l) => [l.materialName, l.unit])),
    deliverySuggestions: data.materialSuggestions,
    deliveredAmounts: buildDeliveredAmounts(data.deliveries),
  };
}
